import React, { useState } from 'react';
import { motion } from 'motion/react';
import { User, X, AlertCircle, Eye, EyeOff, Check } from 'lucide-react';
import { useAuthStore } from '../../store/useAuthStore.js';
import { useNotificationStore } from '../../../notifications/store/useNotificationStore.js';
import { RoleSelectorDropdown } from './RoleSelectorDropdown.js';
import { authApi } from '../../api/authApi.js';
import type { Role } from '../../../../shared/types/index.js';

interface RegisterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenLogin: () => void;
  onLoginSuccess?: () => void;
}

export const RegisterModal: React.FC<RegisterModalProps> = ({
  isOpen,
  onClose,
  onOpenLogin,
  onLoginSuccess,
}) => {
  const { setAuth } = useAuthStore();
  const showToast = useNotificationStore((state) => state.showToast);

  const [regName, setRegName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regConfirmPassword, setRegConfirmPassword] = useState('');
  const [showRegPassword, setShowRegPassword] = useState(false);
  const [showRegConfirmPassword, setShowRegConfirmPassword] = useState(false);
  const [regRole, setRegRole] = useState<Role>('penulis');
  const [isRoleDropdownOpen, setIsRoleDropdownOpen] = useState(false);
  const [regOrg, setRegOrg] = useState('');
  const [regError, setRegError] = useState<string | null>(null);
  const [isRegistering, setIsRegistering] = useState(false);

  if (!isOpen) return null;

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setRegError(null);

    if (regPassword.length < 6) {
      setRegError('Kata sandi minimal 6 karakter');
      return;
    }

    if (regPassword !== regConfirmPassword) {
      setRegError('Konfirmasi kata sandi tidak cocok dengan kata sandi');
      return;
    }

    if ((regRole === 'penerbit' || regRole === 'organisasi') && !regOrg.trim()) {
      setRegError(
        `Nama ${regRole === 'organisasi' ? 'Organisasi / Komunitas' : 'Lembaga Penerbit Resmi'} wajib diisi.`
      );
      return;
    }

    setIsRegistering(true);
    try {
      const data = await authApi.register({
        name: regName.trim(),
        email: regEmail.trim(),
        password: regPassword,
        role: regRole,
        organization: regRole === 'penulis' ? undefined : regOrg.trim() || undefined,
      });
      setRegName('');
      setRegEmail('');
      setRegPassword('');
      setRegConfirmPassword('');
      setRegOrg('');
      setRegError(null);

      setAuth(data.user, data.token);
      showToast('Registrasi Berhasil', `Akun ${data.user.name} berhasil terdaftar.`, 'success');
      onClose();
      onLoginSuccess?.();
    } catch (err: any) {
      setRegError(err.message || 'Gagal mendaftarkan akun baru');
    } finally {
      setIsRegistering(false);
    }
  };

  return (
    <motion.div
      key="register-backdrop"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.18 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 backdrop-blur-xs p-4 overflow-y-auto"
    >
      <motion.div
        key="register-dialog"
        initial={{ opacity: 0, scale: 0.94, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.94, y: 16 }}
        transition={{ type: 'spring', stiffness: 380, damping: 28 }}
        className="bg-white rounded-2xl max-w-md w-full shadow-2xl border border-slate-100 my-auto flex flex-col"
      >
        <div className="p-4 sm:p-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/70 shrink-0 rounded-t-2xl">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-indigo-600 text-white flex items-center justify-center">
              <User className="w-4 h-4" />
            </div>
            <h3 className="font-bold text-slate-900 text-base">Daftar Akun Baru</h3>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 min-w-[44px] min-h-[44px] rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-200/60 flex items-center justify-center cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto max-h-[calc(100vh-120px)] sm:max-h-none">
          <form onSubmit={handleRegisterSubmit} className="p-5 sm:p-6 space-y-3.5">
            {regError && (
              <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 text-xs flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
                <span>{regError}</span>
              </div>
            )}

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Nama Lengkap / Instansi
              </label>
              <input
                type="text"
                required
                value={regName}
                onChange={(e) => setRegName(e.target.value)}
                placeholder="Contoh: Ahmad Fauzan atau Penerbit Mandiri"
                autoComplete="name"
                className="w-full px-3.5 py-2.5 min-h-[44px] text-sm border border-slate-300 rounded-xl focus:outline-hidden focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-600"
              />
            </div>

            <RoleSelectorDropdown
              selectedRole={regRole}
              isOpen={isRoleDropdownOpen}
              onToggle={() => setIsRoleDropdownOpen(!isRoleDropdownOpen)}
              onClose={() => setIsRoleDropdownOpen(false)}
              onSelectRole={setRegRole}
            />

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Email</label>
              <input
                type="email"
                required
                value={regEmail}
                onChange={(e) => setRegEmail(e.target.value)}
                placeholder="nama@domain.com"
                autoComplete="email"
                className="w-full px-3.5 py-2.5 min-h-[44px] text-sm border border-slate-300 rounded-xl focus:outline-hidden focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-600"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Kata Sandi</label>
              <div className="relative">
                <input
                  id="reg-password"
                  type={showRegPassword ? 'text' : 'password'}
                  required
                  value={regPassword}
                  onChange={(e) => setRegPassword(e.target.value)}
                  placeholder="Minimal 6 karakter"
                  autoComplete="new-password"
                  className="w-full px-3.5 py-2.5 min-h-[44px] pr-10 text-sm border border-slate-300 rounded-xl focus:outline-hidden focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-600"
                />
                <button
                  type="button"
                  onClick={() => setShowRegPassword(!showRegPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1 cursor-pointer transition min-h-[44px] min-w-[44px] flex items-center justify-center"
                  title={showRegPassword ? 'Sembunyikan kata sandi' : 'Tampilkan kata sandi'}
                >
                  {showRegPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Konfirmasi Kata Sandi
              </label>
              <div className="relative">
                <input
                  id="reg-confirm-password"
                  type={showRegConfirmPassword ? 'text' : 'password'}
                  required
                  value={regConfirmPassword}
                  onChange={(e) => setRegConfirmPassword(e.target.value)}
                  placeholder="Ketik ulang kata sandi Anda"
                  autoComplete="new-password"
                  className={`w-full px-3.5 py-2.5 min-h-[44px] pr-10 text-sm border rounded-xl focus:outline-hidden focus:ring-2 transition ${
                    regConfirmPassword && regPassword
                      ? regPassword === regConfirmPassword
                        ? 'border-emerald-400 focus:border-emerald-500 focus:ring-emerald-500/20 bg-emerald-50/20'
                        : 'border-rose-400 focus:border-rose-500 focus:ring-rose-500/20 bg-rose-50/20'
                      : 'border-slate-300 focus:border-indigo-600 focus:ring-indigo-500/30'
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowRegConfirmPassword(!showRegConfirmPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1 cursor-pointer transition min-h-[44px] min-w-[44px] flex items-center justify-center"
                  title={
                    showRegConfirmPassword
                      ? 'Sembunyikan konfirmasi kata sandi'
                      : 'Tampilkan konfirmasi kata sandi'
                  }
                >
                  {showRegConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {regConfirmPassword && (
                <div className="mt-1 flex items-center gap-1.5 text-[11px] font-medium animate-in fade-in duration-150">
                  {regPassword === regConfirmPassword ? (
                    <span className="text-emerald-600 flex items-center gap-1">
                      <Check className="w-3.5 h-3.5" />
                      <span>Kata sandi cocok</span>
                    </span>
                  ) : (
                    <span className="text-rose-600 flex items-center gap-1">
                      <AlertCircle className="w-3.5 h-3.5" />
                      <span>Kata sandi belum cocok</span>
                    </span>
                  )}
                </div>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs font-semibold text-slate-700">
                  {regRole === 'organisasi'
                    ? 'Nama Organisasi / Komunitas'
                    : regRole === 'penerbit'
                    ? 'Nama Lembaga Penerbit Resmi'
                    : 'Organisasi / Komunitas'}
                  {regRole !== 'penulis' && <span className="text-rose-500 ml-1">*</span>}
                </label>
                {regRole === 'penulis' && (
                  <span className="text-[10px] text-slate-400 font-medium bg-slate-100 px-2 py-0.5 rounded">
                    Dinonaktifkan
                  </span>
                )}
              </div>
              <input
                type="text"
                disabled={regRole === 'penulis'}
                value={regRole === 'penulis' ? '' : regOrg}
                onChange={(e) => setRegOrg(e.target.value)}
                placeholder={
                  regRole === 'organisasi'
                    ? 'Contoh: Komunitas Penulis Muda, Forum Guru Literasi'
                    : regRole === 'penerbit'
                    ? 'Contoh: Pustaka Abadi Nusantara, PT / CV'
                    : 'Dinonaktifkan untuk Penulis Mandiri'
                }
                autoComplete="organization"
                className={`w-full px-3.5 py-2 text-sm border rounded-xl transition duration-150 ${
                  regRole === 'penulis'
                    ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed select-none'
                    : 'bg-white border-slate-300 focus:outline-hidden focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-600'
                }`}
              />
              <p className="text-[11px] text-slate-500 mt-1 leading-snug">
                {regRole === 'penulis'
                  ? 'Input organisasi/komunitas dinonaktifkan khusus untuk Penulis Mandiri.'
                  : 'Nama yang didaftarkan akan dikunci dan tidak bisa diubah setelah diverifikasi dan disetujui oleh admin.'}
              </p>
            </div>

            <button
              type="submit"
              disabled={isRegistering}
              className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-semibold text-sm transition shadow-xs disabled:opacity-50 mt-2 cursor-pointer"
            >
              {isRegistering ? 'Mendaftarkan...' : 'Daftar Akun'}
            </button>

            <p className="text-xs text-center text-slate-500 pt-1 pb-2">
              Sudah memiliki akun?{' '}
              <button
                type="button"
                onClick={() => {
                  onClose();
                  onOpenLogin();
                }}
                className="text-indigo-600 font-semibold hover:underline cursor-pointer"
              >
                Masuk di sini
              </button>
            </p>
          </form>
        </div>
      </motion.div>
    </motion.div>
  );
};
